// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import type {GuildBan} from '@app/features/guild/commands/GuildCommands';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import {BannedUserActionsSheet} from '@app/features/guild/components/modals/guild_tabs/BannedUserActionsSheet';
import styles from '@app/features/guild/components/modals/guild_tabs/MemberListStyles.module.css';
import {UserListItem} from '@app/features/guild/components/modals/guild_tabs/UserListItem';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {BannedUserContextMenu} from '@app/features/ui/action_menu/BannedUserContextMenu';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input} from '@app/features/ui/components/form/Input';
import {Spinner} from '@app/features/ui/components/Spinner';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {MagnifyingGlassIcon, ProhibitIcon} from '@phosphor-icons/react';
import {matchSorter} from 'match-sorter';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

const logger = new Logger('GuildBansTab');
const GuildBansTab: React.FC<{guildId: string}> = observer(({guildId}) => {
	const {t} = useLingui();
	const [bans, setBans] = useState<Array<GuildBan>>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [activeMenuUserId, setActiveMenuUserId] = useState<string | null>(null);
	const [activeSheetBan, setActiveSheetBan] = useState<GuildBan | null>(null);
	const isMobile = MobileLayout.enabled;
	const formatTag = useCallback(
		(ban: GuildBan) => ban.user.tag ?? `${ban.user.username}#${(ban.user.discriminator ?? '').padStart(4, '0')}`,
		[],
	);
	const fetchBans = useCallback(async () => {
		setIsLoading(true);
		try {
			const fetchedBans = await GuildCommands.fetchBans(guildId);
			setBans(fetchedBans);
		} catch (error) {
			logger.error('Failed to fetch bans', error);
			ToastCommands.createToast({
				type: 'error',
				children: <Trans>Failed to load bans. Please try again.</Trans>,
			});
		} finally {
			setIsLoading(false);
		}
	}, [guildId]);
	useEffect(() => {
		fetchBans();
	}, [fetchBans]);
	const filteredBans = useMemo(() => {
		if (!searchQuery) return bans;
		return matchSorter(bans, searchQuery, {
			keys: [(ban) => ban.user.username, (ban) => formatTag(ban), (ban) => ban.reason || ''],
		});
	}, [bans, searchQuery]);
	const handleUnban = useCallback(
		(ban: GuildBan) => {
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Revoke Ban`}
						description={
							<div>
								<Trans>
									Are you sure you want to revoke the ban for <strong>{ban.user.username}</strong>? They will be able to
									rejoin the community.
								</Trans>
							</div>
						}
						primaryText={t`Revoke Ban`}
						primaryVariant="danger-primary"
						secondaryText={t`Cancel`}
						onPrimary={async () => {
							try {
								await GuildCommands.unbanMember(guildId, ban.user.id);
								ToastCommands.createToast({
									type: 'success',
									children: <Trans>Successfully revoked ban for {ban.user.username}</Trans>,
								});
								await fetchBans();
							} catch (error) {
								logger.error('Failed to unban member', error);
								ToastCommands.createToast({
									type: 'error',
									children: <Trans>Failed to revoke ban. Please try again.</Trans>,
								});
							}
						}}
					/>
				)),
			);
		},
		[guildId, fetchBans],
	);
	const handleBanContextMenu = useCallback(
		(ban: GuildBan, event: React.MouseEvent<HTMLElement>, fromButton?: boolean) => {
			if (fromButton) {
				setActiveMenuUserId(ban.user.id);
			} else {
				setActiveMenuUserId(null);
			}
			ContextMenuCommands.openFromEvent(
				event,
				({onClose}) => <BannedUserContextMenu ban={ban} onClose={onClose} onRevoke={() => handleUnban(ban)} />,
				{onClose: () => setActiveMenuUserId(null)},
			);
		},
		[handleUnban],
	);
	if (isLoading) {
		return (
			<div className={styles.loadingContainer}>
				<Spinner />
				<p className={styles.loadingText}>
					<Trans>Loading bans...</Trans>
				</p>
			</div>
		);
	}
	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<h2 className={styles.title}>
					<Trans>Banned Users</Trans>
				</h2>
				<p className={styles.subtitle}>
					<Trans>View and manage banned users.</Trans>
				</p>
			</div>
			<div className={styles.controls}>
				<Input
					type="text"
					placeholder={t`Search bans...`}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					leftIcon={<MagnifyingGlassIcon size={16} weight="bold" />}
					className={styles.searchInput}
				/>
			</div>
			{bans.length === 0 && (
				<StatusSlate
					Icon={ProhibitIcon}
					title={<Trans>No Banned Users</Trans>}
					description={<Trans>No users are currently banned from this community.</Trans>}
					fullHeight={true}
				/>
			)}
			{filteredBans.length === 0 && searchQuery && bans.length > 0 && (
				<div className={styles.notice}>
					<p className={styles.noticeText}>
						<Trans>No bans found matching your search.</Trans>
					</p>
				</div>
			)}
			{filteredBans.length > 0 && (
				<div className={styles.scrollContainer}>
					<div>
						<div className={styles.memberList}>
							<div className={styles.memberGroup}>
								{filteredBans.map((ban, index) => {
									const userTag = formatTag(ban);
									const avatarUrl = AvatarUtils.getUserAvatarURL(ban.user, false);
									return (
										<React.Fragment key={ban.user.id}>
											<UserListItem
												user={ban.user}
												avatarUrl={avatarUrl}
												displayName={ban.user.username}
												tag={userTag}
												isMobile={isMobile}
												isMenuActive={activeMenuUserId === ban.user.id}
												onContextMenu={(e, fromButton) => handleBanContextMenu(ban, e, fromButton)}
												onActivate={() => setActiveSheetBan(ban)}
											/>
											{index < filteredBans.length - 1 && <div className={styles.divider} />}
										</React.Fragment>
									);
								})}
							</div>
						</div>
					</div>
				</div>
			)}
			{activeSheetBan && (
				<BannedUserActionsSheet
					isOpen={true}
					onClose={() => setActiveSheetBan(null)}
					ban={activeSheetBan}
					onRevoke={() => handleUnban(activeSheetBan)}
				/>
			)}
		</div>
	);
});

export default GuildBansTab;
